import { Slot } from '@radix-ui/react-slot'
import { Loader2 } from 'lucide-react'
import { forwardRef, type ButtonHTMLAttributes } from 'react'

import { cn } from './utils'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline' | 'danger' | 'link'
export type ButtonSize = 'sm' | 'md' | 'lg' | 'xl' | 'icon'

const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium transition-[background-color,color,box-shadow,transform] ' +
  'select-none disabled:pointer-events-none disabled:opacity-50 active:translate-y-px focus-visible:focus-ring ' +
  '[&_svg]:pointer-events-none [&_svg]:shrink-0'

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-accent text-accent-ink shadow-card hover:bg-accent-hover',
  secondary: 'bg-paper-2 text-ink border border-line hover:bg-paper-3',
  outline: 'bg-transparent text-ink border border-line-strong hover:bg-paper-2',
  ghost: 'bg-transparent text-ink hover:bg-paper-2',
  danger: 'bg-danger text-white hover:brightness-95',
  link: 'bg-transparent text-accent underline-offset-4 hover:underline px-0',
}

const sizes: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm [&_svg]:size-4',
  md: 'h-11 px-4 text-sm [&_svg]:size-4',
  lg: 'h-12 px-5 text-base [&_svg]:size-5',
  xl: 'h-16 px-7 text-lg rounded-2xl [&_svg]:size-6',
  icon: 'size-11 [&_svg]:size-5',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Render the child element instead of a `<button>` (for `<Link>`). */
  asChild?: boolean
  loading?: boolean
}

/** Every hit target is ≥ 44 px tall by default (`md`); `xl` is the Home-page size. */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant = 'secondary', size = 'md', asChild = false, loading = false, disabled, children, ...props },
  ref,
) {
  const classes = cn(base, variants[variant], sizes[size], className)
  if (asChild) {
    // Radix Slot needs exactly one element child, so the loader is not rendered here.
    return (
      <Slot ref={ref} className={classes} aria-disabled={disabled || loading || undefined} aria-busy={loading || undefined} {...props}>
        {children}
      </Slot>
    )
  }
  return (
    <button ref={ref} type={props.type ?? 'button'} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>
      {loading ? <Loader2 className="animate-spin" aria-hidden /> : null}
      {children}
    </button>
  )
})

export function buttonClasses(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', className?: string): string {
  return cn(base, variants[variant], sizes[size], className)
}
